import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import getLang from 'I18n/utils/getLang';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIBox from 'UIComponents/layout/UIBox';
import UIFlex from 'UIComponents/layout/UIFlex';
import { LikeLink, RouterLink } from '../../../components/AcademyLinks';
import { FormattedMessage, FormattedReactMessage } from '../../../components/I18n';
import MobileNestedMenu from '../../../components/Modals/FullScreenModal/MobileNestedMenu';
import PanelSection from '../../../components/Modals/FullScreenModal/PanelSection';
import styled, { css } from '../../../utils/Styling';
import UsageTracker from '../../../utils/UsageTracker';
import { AddToPlaylist, CardMenuWatchLaterOption, ChangePlaylistThumbnail, CreatePlaylistLink, EditPlaylistButton, SavePlaylistButton, useUserOwnsPlaylist } from '../../savedItems';
import StartPlaylistButton from '../../savedItems/components/StartPlaylistButton';
import { ShareButton } from '../../share';
import { OverviewSource } from '../../tracks';
const MenuRowStyle = css(["display:flex;flex-grow:1;justify-content:space-between;font-weight:initial;padding:12px 20px !important;"]);
export const MenuRow = styled.div.withConfig({
  displayName: "MobileCardMenu__MenuRow",
  componentId: "g7k8bx-0"
})(["", ""], MenuRowStyle);
export const MenuButton = styled(UIButton).withConfig({
  displayName: "MobileCardMenu__MenuButton",
  componentId: "g7k8bx-1"
})(["", ""], MenuRowStyle);
export const MenuLink = styled(RouterLink).withConfig({
  displayName: "MobileCardMenu__MenuLink",
  componentId: "g7k8bx-2"
})(["", ""], MenuRowStyle);
export const MenuLikeLink = styled(LikeLink).withConfig({
  displayName: "MobileCardMenu__MenuLikeLink",
  componentId: "g7k8bx-3"
})(["", " font-weight:500;"], MenuRowStyle);

const MobileCardShareButton = shareButtonProps => /*#__PURE__*/_jsx(ShareButton, Object.assign({}, shareButtonProps, {
  render: button => /*#__PURE__*/_jsx(MenuRow, {
    children: button
  })
}));

const TrackLessonMenuOptions = ({
  trackId,
  lessonId,
  language = getLang(),
  onOpenChange,
  children
}) => {
  const closeModal = () => onOpenChange(false);

  const onAddToPlaylistClick = () => {
    closeModal();
    UsageTracker.track('playlistInteraction', {
      action: 'add-to-playlist',
      location: 'card-menu'
    });
  };

  const onAddToNewPlaylistClick = () => {
    closeModal();
    UsageTracker.track('playlistInteraction', {
      action: 'add-to-new-playlist',
      location: 'card-menu'
    });
  };

  return /*#__PURE__*/_jsxs(MobileNestedMenu, {
    indented: false,
    open: true,
    setIsOpen: onOpenChange,
    useBackText: true,
    useSidePanel: true,
    children: [/*#__PURE__*/_jsx(PanelSection, {
      showBorders: false,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academyNav.heading.favorites"
      })
    }), /*#__PURE__*/_jsx(CardMenuWatchLaterOption, {
      lessonId: lessonId,
      onClick: closeModal,
      render: content => /*#__PURE__*/_jsx(MenuRow, {
        children: content
      }),
      trackId: trackId
    }), /*#__PURE__*/_jsxs(MenuButton // @ts-expect-error Untyped
    , {
      subpage: /*#__PURE__*/_jsx(AddToPlaylist, {
        language: language,
        lessonId: lessonId,
        loadMode: "button",
        loadMoreButton: /*#__PURE__*/_jsx(MenuLikeLink, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.cardMenu.loadMorePlaylists"
          })
        }),
        onPlaylistClick: onAddToPlaylistClick,
        renderList: content => /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(PanelSection, {
            showBorders: false,
            title: /*#__PURE__*/_jsxs(UIFlex, {
              children: [/*#__PURE__*/_jsx(UIBox, {
                grow: 1,
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "academyNav.heading.myPlaylists"
                })
              }), /*#__PURE__*/_jsx(CreatePlaylistLink, {
                language: language,
                lessonId: lessonId,
                render: (linkContent, to) => /*#__PURE__*/_jsx(RouterLink, {
                  onClick: onAddToNewPlaylistClick,
                  to: to,
                  truncate: true,
                  children: linkContent
                }),
                trackId: trackId,
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "learningCenter.cardMenu.addToNewPlaylist"
                })
              })]
            })
          }), content]
        }),
        renderRow: (content, key) => /*#__PURE__*/_jsx(MenuRow, {
          children: content
        }, key),
        trackId: trackId
      }),
      use: "unstyled",
      children: [/*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "learningCenter.cardMenu.addToPlaylist"
      }), /*#__PURE__*/_jsx(UIIcon, {
        name: "right"
      })]
    }), /*#__PURE__*/_jsx(CreatePlaylistLink, {
      language: language,
      lessonId: lessonId,
      render: (linkContent, to) => /*#__PURE__*/_jsx(MenuLink, {
        onClick: onAddToNewPlaylistClick,
        to: to,
        truncate: true,
        use: "unstyled",
        children: linkContent
      }),
      trackId: trackId,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.cardMenu.addToNewPlaylist"
      })
    }), /*#__PURE__*/_jsx(PanelSection, {
      showBorders: false,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.lessonInformation.mobileMenuContentHeader"
      })
    }), /*#__PURE__*/_jsx(MobileCardShareButton, {
      language: language,
      lessonId: lessonId,
      onClick: closeModal,
      trackId: trackId
    }), children]
  });
};

const PlaylistMenuOptions = ({
  playlistId,
  language = getLang(),
  onOpenChange,
  children
}) => {
  const userIsOwner = useUserOwnsPlaylist(playlistId);

  const closeModal = () => onOpenChange(false);

  return /*#__PURE__*/_jsxs(MobileNestedMenu, {
    indented: false,
    open: true,
    setIsOpen: onOpenChange,
    useBackText: true,
    useSidePanel: true,
    children: [/*#__PURE__*/_jsx(PanelSection, {
      showBorders: false,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.playlists.playlist"
      })
    }), /*#__PURE__*/_jsx(MenuRow, {
      children: /*#__PURE__*/_jsx(StartPlaylistButton, {
        appearsIn: OverviewSource.Playlist,
        playlistId: playlistId,
        showLoader: true,
        style: {
          textDecoration: 'none',
          fontWeight: 'normal',
          width: '100%'
        },
        use: "unstyled"
      })
    }), userIsOwner && /*#__PURE__*/_jsx(MenuRow, {
      children: /*#__PURE__*/_jsx(EditPlaylistButton, {
        playlistId: playlistId,
        style: {
          fontWeight: 'normal',
          width: '100%',
          textDecoration: 'none'
        },
        use: "unstyled"
      })
    }), userIsOwner && /*#__PURE__*/_jsxs(MenuButton // @ts-expect-error Untyped
    , {
      subpage: /*#__PURE__*/_jsx(ChangePlaylistThumbnail, {
        onItemClick: closeModal,
        playlistId: playlistId,
        renderList: content => /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(PanelSection, {
            showBorders: false,
            title: /*#__PURE__*/_jsx(UIFlex, {
              children: /*#__PURE__*/_jsx(UIBox, {
                grow: 1,
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "learningCenter.cardMenu.playlistItems"
                })
              })
            })
          }), content]
        }),
        renderRow: (content, key) => /*#__PURE__*/_jsx(MenuRow, {
          children: content
        }, key)
      }),
      use: "unstyled",
      children: [/*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "learningCenter.cardMenu.changeThumbnail"
      }), /*#__PURE__*/_jsx(UIIcon, {
        name: "right"
      })]
    }), /*#__PURE__*/_jsx(PanelSection, {
      showBorders: false,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.lessonInformation.mobileMenuContentHeader"
      })
    }), /*#__PURE__*/_jsx(MobileCardShareButton, {
      onClick: closeModal,
      playlistId: playlistId
    }), /*#__PURE__*/_jsx(PanelSection, {
      showBorders: false,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academyNav.heading.favorites"
      })
    }), /*#__PURE__*/_jsx(SavePlaylistButton, {
      language: language,
      onClick: closeModal,
      playlistId: playlistId,
      render: content => /*#__PURE__*/_jsx(MenuRow, {
        children: content
      }),
      showTooltip: true,
      style: {
        width: '100%'
      }
    }), children]
  });
};

const MobileCardMenu = (_ref) => {
  let {
    children,
    isOpen,
    renderExtraOptions
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, ["children", "isOpen", "renderExtraOptions"]);

  const {
    playlistId,
    onOpenChange
  } = props;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [children, isOpen ? playlistId ? /*#__PURE__*/_jsx(PlaylistMenuOptions, Object.assign({
      playlistId: playlistId
    }, props, {
      children: renderExtraOptions && renderExtraOptions(onOpenChange)
    })) : /*#__PURE__*/_jsx(TrackLessonMenuOptions, Object.assign({}, props, {
      children: renderExtraOptions && renderExtraOptions(onOpenChange)
    })) : null]
  });
};

export default MobileCardMenu;